import { InMemoryCache, type InMemoryCacheConfig } from '@apollo/client/cache';
import {
	ApolloClient,
	type ApolloClientOptions,
	createHttpLink,
	from,
	split,
} from '@apollo/client/core';
import { onError } from '@apollo/client/link/error';
import { SuiMessageTypeType, type SuiMessageType } from '@developers/scoro-ui';
import { persistCache } from 'apollo3-cache-persist';
import { EncryptStorage } from 'encrypt-storage';
import { type I18n } from 'vue-i18n';
import { BatchHttpLink } from '@apollo/client/link/batch-http';
import EXTENDED_SCHEMA from '@/lib/apollo/clientSideSchema.graphql';
import { persistenceMapper, createPersistLink } from './persist';
import { EncryptStorageWrapper } from './persist/EncryptStorageWrapper';
import { useCompanyAccountStore } from '@/features/CompanyAccount';

import { type RoleProject, TYPES, type User } from '@/generated/graphql';
import { useMessagesStore } from '@/features/Message/stores';
import { SAPI_ERROR_MESSAGE_ID } from '@/features/Message';
import {
	CODE_INVALID_ARGUMENT,
	CODE_INVALID_OPERATION,
	CODE_GROUPING_NOT_ALLOWED,
	CODE_DATA_NOT_FOUND,
} from './errorCodes';
import { usePageUnloadStore } from '@/stores/pageUnload';
import { useMfeUtils } from '@/composables';
import {
	concatPaginationUnique,
	detailsPrefilledWithCachedData,
} from './apolloTools';

const IGNORED_RESPONSE_STATUS_CODES = [400, 401, 403, 404];

/**
 * Create a new cache instance for Apollo Client.
 *
 * This is a separate function so that the same cache implementation can be
 * used in tests.
 *
 * https://www.apollographql.com/docs/react/caching/cache-configuration/
 */
export const createApolloCache = (config: InMemoryCacheConfig = {}) => {
	const cache = new InMemoryCache({
		// In order to make use of fragments with unions and interfaces, we
		// need to configure which types extend which interfaces
		// https://www.apollographql.com/docs/react/data/fragments/#using-fragments-with-unions-and-interfaces
		possibleTypes: {
			FilterInterface: [
				'NumberRangeFilter',
				'DateRangeFilter',
				'MultiSelectFilter',
				'SingleSelectFilter',
			],

			Notification: ['Notification'],
		},
		typePolicies: {
			Query: {
				fields: {
					QuotesList: concatPaginationUnique(['filters']),
					NotificationsList: concatPaginationUnique(['filters']),
					TasksList: concatPaginationUnique(['input', ['filters']]),
					Quote: {
						read: detailsPrefilledWithCachedData(
							TYPES.Quote,
							'id number'
						),
					},
					PurchaseOrder: {
						read: detailsPrefilledWithCachedData(
							TYPES.PurchaseOrder,
							'id number'
						),
					},
					Trigger: {
						read: detailsPrefilledWithCachedData(
							TYPES.Trigger,
							'id'
						),
					},
					Role: {
						read: detailsPrefilledWithCachedData(TYPES.Role, 'id'),
					},
					Task: {
						read: detailsPrefilledWithCachedData(TYPES.Task, 'id'),
					},
				},
			},
			MenuItem: {
				keyFields: ['moduleName'],
			},
			SearchSetting: {
				keyFields: ['feature', ['name']],
			},
			Feature: {
				keyFields: ['name'],
			},
			Permission: {
				keyFields: ['name'],
			},
			Setting: {
				keyFields: ['name'],
			},
			SelfServiceData: {
				keyFields: [],
			},
			SelfServiceAddon: {
				keyFields: ['id', 'code'],
			},
			TriggerField: {
				keyFields: ['name'],
			},
			TriggerFieldValue: {
				keyFields: ['id', 'label'],
			},
			User: {
				fields: {
					profilePicture: {
						merge: true,
					},
				},
			},
			Role: {
				fields: {
					users: {
						merge(_existing: User[], incoming: User[]) {
							return incoming;
						},
					},
				},
			},
			Tab: {
				keyFields: ['key'],
			},
			Project: {
				fields: {
					members: {
						merge(_existing: User[], incoming: User[]) {
							return incoming;
						},
					},
					cupoUsers: {
						merge(_existing: User[], incoming: User[]) {
							return incoming;
						},
					},
					roleProjects: {
						merge(
							_existing: RoleProject[],
							incoming: RoleProject[]
						) {
							return incoming;
						},
					},
					permissions: {
						merge: true,
					},
				},
			},
			ProjectHealth: {
				keyFields: ['project', ['id']],
			},
			ChecklistItems: {
				keyFields: ['item'],
			},
			Task: {
				fields: {
					assignees: {
						merge(_existing: User[], incoming: User[]) {
							return incoming;
						},
					},
				},
			},
		},
		...config,
	});

	return cache;
};

/** NOTE: this can be changed to queries to include */
const batchExcludeQueryNames: string[] = [
	'projectDetailsHeader',
	'resourcePlanningPortfolioResourceUtilization',
	'resourcePlanningUtilization',
	'eliInsightsBookmark',
	'projectTabsPage',
	'projectHealth',
	'eliWelcomeMessage',
];

/**
 * Create the Apollo Client instance so that it can be used in our micro
 * front-ends.
 */
export const createApolloClient = async (
	i18n: I18n<
		Record<string, unknown>,
		Record<string, unknown>,
		Record<string, unknown>,
		string,
		false
	>
) => {
	const messagesStore = useMessagesStore();
	const { getCSRFToken } = useMfeUtils();

	const companyAccountStore = useCompanyAccountStore();
	companyAccountStore.setCurrentAccountFromCookie();

	const pageUnloadStore = usePageUnloadStore();

	/**
	 * A basic global error handler that just shows a message to the user via
	 * Pinia.
	 */
	const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
		// BE SapiException error code constants are defined in SapiException.php
		// and duplicated in errorCodes.ts, which is imported here

		// when our graphql endpoints are unreachable, then most likely it's due to no internet connection
		if (
			networkError &&
			'statusCode' in networkError &&
			IGNORED_RESPONSE_STATUS_CODES.includes(networkError.statusCode)
		) {
			return;
		}

		if (operation.getContext().handleErrorLocally) {
			return;
		}

		let errorMessage: SuiMessageType | undefined;

		if (graphQLErrors) {
			let lastErrorCode = null;
			let message = i18n.global.t('general_unexpected_error');
			const errorMessages: string[] = [];

			graphQLErrors.forEach((err) => {
				lastErrorCode = err.extensions?.code;

				if (err.extensions?.translation) {
					errorMessages.push(
						i18n.global.t(
							err.extensions.translation as string,
							err.extensions?.translation_fields as Record<
								string,
								unknown
							>
						)
					);
				}
			});

			if (errorMessages.length > 0) {
				// TODO: refactor the MessagesList component to allow HTML and then either join by '<br>' or even wrap these in a list
				message = errorMessages.join(' ');
			} else if (lastErrorCode) {
				switch (lastErrorCode) {
					case CODE_INVALID_ARGUMENT:
						message = i18n.global.t(
							'general_invalid_argument_error'
						);
						break;
					case CODE_INVALID_OPERATION:
						message = i18n.global.t(
							'general_invalid_operation_error'
						);
						break;
					case CODE_DATA_NOT_FOUND:
						message = i18n.global.t('general_data_not_found_error');
						break;
					default:
				}
			}

			errorMessage = {
				id: SAPI_ERROR_MESSAGE_ID,
				title: '',
				message,
				type: SuiMessageTypeType.Error,
			};

			// if error is "GROUPING_NOT_ALLOWED", do not display
			// generic floating error message
			if (lastErrorCode === CODE_GROUPING_NOT_ALLOWED) {
				errorMessage = undefined;
			}
		}

		// in case user causes a page refresh on click, don't show the network error
		// pageUnloadStore.isPageUnloading value is updated from the monolith on beforeunload event
		if (networkError && !pageUnloadStore.isPageUnloading) {
			errorMessage = {
				id: SAPI_ERROR_MESSAGE_ID,
				title: '',
				message: i18n.global.t('general_unexpected_error'),
				type: SuiMessageTypeType.Error,
			};
		}

		if (errorMessage) {
			messagesStore.addMessage(errorMessage);
		}
	});

	/**
	 * The HTTP link configures the details of how Apollo Client makes
	 * requests.
	 *
	 * https://www.apollographql.com/docs/link/links/http/
	 */
	const httpLink = createHttpLink({
		uri: '/graphql',
	});

	/**
	 * The HTTP link batching configures the details of how Apollo Client makes
	 * combined requests.
	 *
	 * https://www.apollographql.com/docs/react/api/link/apollo-link-batch-http
	 */
	const batchLink = new BatchHttpLink({
		uri: '/graphql',
		batchMax: 5,
		batchInterval: 50,
	});

	/**
	 * Add query name to batchExcludeQueryNames to exclude query from batching
	 */
	const directionalLink = split(
		(operation) =>
			!batchExcludeQueryNames.includes(operation.operationName),
		batchLink,
		httpLink
	);

	const cache = createApolloCache();

	const encryptionKey = getCSRFToken();
	if (encryptionKey) {
		await persistCache({
			cache,
			// Encrypt the persisted storage with the CSRF token which is
			// cleared on log out. This is not actually that secure, but at
			// least it's less obvious when looking at the console. Also, since
			// the key is removed when the user logs out, it should not be
			// possible to decrypt the data afterwards (unless the token has
			// been saved).
			storage: new EncryptStorageWrapper(
				new EncryptStorage(encryptionKey)
			),
			persistenceMapper,
		});
	}

	const persistLink = createPersistLink();

	/**
	 * Final configuration output for creating an apollo client instance
	 * In production mode, client-side type definitions and resolvers should be omitted
	 * Otherwise we excessively bloat the final bundle with faker factories!
	 */
	let apolloOptions: ApolloClientOptions<unknown> = {
		link: from([persistLink, errorLink, directionalLink]),
		queryDeduplication: true,
		cache,
		connectToDevTools: process.env.NODE_ENV === 'development',
	};

	if (process.env.VITE_SCORO_ENV === 'development') {
		// If we do the top level import, faker will be bundled into production
		// build as the dependency because it is used for factories in client
		// side schema mocks
		const module = await import('./clientSideSchemaImplementation');
		apolloOptions = {
			...apolloOptions,
			typeDefs: EXTENDED_SCHEMA,
			resolvers: module.createClientSideResolvers(),
		};
	}

	const apollo = new ApolloClient(apolloOptions);

	return apollo;
};
